// Map widget embedded in the General Overview tab. Composes:
//   • FilterStrip (search + Entity / State / Category multi-selects +
//     Color by toggle) — reused as-is from the Locations module
//   • MapView (Leaflet map with coloured pins + interactive legend) —
//     also reused from Locations
//   • SiteOverviewPanel (right-side drawer with per-site KPIs) —
//     opens when a pin or filter chip is clicked
//
// Gated by `overview:read_map`; permission check is done by the
// parent so this component just renders.

import { useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, Stack, Typography, alpha } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import { useQuery } from '@tanstack/react-query'

import type { Site } from '@cmms/types'
import { api } from '../../../lib/api'
import { MapView } from '../../locations/components/MapView'
import { FilterStrip } from '../../locations/components/FilterStrip'
import {
  buildLegend, colorForValue, getDimensionValue, type ColorBy,
} from '../../locations/lib/siteColors'
import { SiteOverviewPanel } from './SiteOverviewPanel'

export function OverviewMapSection() {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  // Local filter state — independent of the LocationsPage state so
  // that browsing the Overview doesn't perturb the Locations filters.
  const [search, setSearch] = useState('')
  const [entityFilter, setEntityFilter] = useState<string[]>([])
  const [stateFilter, setStateFilter] = useState<string[]>([])
  const [categoryFilter, setCategoryFilter] = useState<string[]>([])
  const [colorBy, setColorBy] = useState<ColorBy>('none')
  const [highlightedLegendValue, setHighlightedLegendValue] = useState<string | null>(null)
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null)

  // Whenever the color-by dimension changes, the previously-highlighted
  // legend value is no longer meaningful.
  useEffect(() => { setHighlightedLegendValue(null) }, [colorBy])

  const sitesQ = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.sites.list(),
    staleTime: 5 * 60_000,
  })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  // Combined filter: search (name OR address) AND each multi-select
  // dimension (OR within, AND across).
  const filteredSites = useMemo(() => {
    const q = search.trim().toLowerCase()
    const entitySet   = new Set(entityFilter)
    const stateSet    = new Set(stateFilter)
    const categorySet = new Set(categoryFilter)
    return sites.filter((s) => {
      if (q) {
        const hay = [s.name, s.address].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      if (entitySet.size > 0   && !(s.entity   && entitySet.has(s.entity)))     return false
      if (stateSet.size > 0    && !(s.state    && stateSet.has(s.state)))       return false
      if (categorySet.size > 0 && !(s.category && categorySet.has(s.category))) return false
      return true
    })
  }, [sites, search, entityFilter, stateFilter, categoryFilter])

  const legend = useMemo(
    () => buildLegend(filteredSites, colorBy, isDark),
    [filteredSites, colorBy, isDark]
  )
  const getSiteColor = useMemo(() => {
    if (colorBy === 'none') return undefined
    return (s: Site): string | undefined =>
      colorForValue(getDimensionValue(s, colorBy), isDark)
  }, [colorBy, isDark])
  const getSiteDimensionValue = useMemo(() => {
    if (colorBy === 'none') return undefined
    return (s: Site): string | null => getDimensionValue(s, colorBy)
  }, [colorBy])

  return (
    <Box sx={{
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      borderRadius: 2.5,
      overflow: 'hidden',
      // Fixed height — the Overview page is a scrollable column, so we
      // don't want the map to grow with the data. Tall enough to be
      // useful without dominating the viewport.
      display: 'flex', flexDirection: 'column',
      height: 560,
    }}>
      {/* Section header --------------------------------------- */}
      <Box sx={{
        px: 2.5, py: 1.5,
        borderBottom: '1px solid', borderColor: 'divider',
        background: (t) => t.palette.mode === 'light'
          ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, transparent 100%)`
          : `linear-gradient(180deg, ${alpha(t.palette.common.white, 0.04)} 0%, transparent 100%)`,
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
            color: 'primary.contrastText',
          }}>
            <MapOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1.25 }}>
              Sites map
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 500 }}>
              Filter by entity, state or category, then click a site for a quick overview.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Filter strip ----------------------------------------- */}
      {!sitesQ.isLoading && sites.length > 0 && (
        <FilterStrip
          allSites={sites}
          filteredCount={filteredSites.length}
          isDark={isDark}
          search={search}
          onSearchChange={setSearch}
          onPickSite={setSelectedSiteId}
          entityFilter={entityFilter}
          onEntityFilterChange={setEntityFilter}
          stateFilter={stateFilter}
          onStateFilterChange={setStateFilter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          colorBy={colorBy}
          onColorByChange={setColorBy}
        />
      )}

      {/* Map area — relative parent so the per-site overview can
          overlay it at the bottom-left without a portal. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
        {sitesQ.isLoading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : sites.length === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', color: 'text.secondary', p: 4 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
              No sites yet
            </Typography>
            <Typography sx={{ fontSize: 12.5 }}>
              Add sites in Settings → Sites to populate the map.
            </Typography>
          </Box>
        ) : (
          <>
            <MapView
              sites={filteredSites}
              selectedSiteId={selectedSiteId}
              onSelectSite={setSelectedSiteId}
              getPinColor={getSiteColor}
              colorBy={colorBy}
              legend={legend}
              highlightedLegendValue={highlightedLegendValue}
              onLegendValueClick={setHighlightedLegendValue}
              getDimensionValue={getSiteDimensionValue}
            />
            {/* Per-site overview — floats over the bottom-left of the map. */}
            <SiteOverviewPanel
              siteId={selectedSiteId}
              onClose={() => setSelectedSiteId(null)}
            />
          </>
        )}
      </Box>
    </Box>
  )
}
